/**
 * Domain types.
 *
 * These mirror what the Atlas-backed API returns, which in turn mirrors the pipeline's own
 * item schema. Field names that come straight from the summarizer keep their pipeline names
 * (`num`, `vote`, `startTime`) so a reader can trace them back to
 * `docs/aware-summary-prompt.seed.txt` without a translation step.
 */

/**
 * Government body type is a first-class, extensible dimension — never a binary. County and
 * State exist today as "coming soon": they render in the badge set and appear disabled in
 * pickers, so turning them on is a data change rather than a redesign.
 */
export type BodyType = 'municipal' | 'boe' | 'county' | 'state';

export interface BodyMeta {
  label: string;
  /** Shorter label for dense rows and narrow chips. */
  short: string;
  /** Not yet carrying data — disabled in pickers, still visible. */
  comingSoon: boolean;
}

export const BODY_META: Record<BodyType, BodyMeta> = {
  municipal: { label: 'Municipal', short: 'Municipal', comingSoon: false },
  boe: { label: 'Board of Ed', short: 'Board of Ed', comingSoon: false },
  // Live as of 08/15/2026. County was never absent — 5,487 meetings name a county and none
  // were tagged as one; they sat under `municipal` or `board_of_education`. The API classifies
  // them from the entity name, taking care that a county SCHOOL board stays Board of Ed and a
  // Pennsylvania township's "Board of Supervisors" stays Municipal.
  county: { label: 'County', short: 'County', comingSoon: false },
  // Live as of 08/15/2026. The corpus carries state-level bodies — the New York State Senate
  // among them — from test coverage around state agencies. They arrive tagged "Town
  // Government" at the source, so the API classifies them from the entity name; without that
  // they would wear a Municipal badge.
  //
  // Labelled "State agency" rather than "State": these are agencies and legislative bodies,
  // not a geographic tier like County.
  state: { label: 'State agency', short: 'State', comingSoon: false },
};

export const ACTIVE_BODY_TYPES: readonly BodyType[] = ['municipal', 'boe', 'county', 'state'];

export interface Town {
  id: string;
  name: string;
  /** State, province, council area or nation — the country catalogue supplies the vocabulary. */
  region: string;
  /** Which bodies this town has meetings for. */
  bodies: BodyType[];
  /** Unread recaps since the user last looked. */
  newCount: number;
  /** Total meetings on file. */
  meetingCount: number;
}

export interface Meeting {
  id: string;
  body: BodyType;
  /** The body's own name, e.g. "Township Council" / "Board of Education". */
  bodyName: string;
  townId: string;
  townName: string;
  townRegion: string;
  /** Already formatted mm/dd/yyyy by the API — one formatter, server-side. */
  date: string;
  /** Relative stamp: "2h ago", "yesterday", "3d ago", or a date past 60 days. */
  ago: string;
  /** Epoch ms, for sorting only. Never displayed. */
  sortKey: number;
  /** The name of THIS part of the meeting — the card heading. */
  title: string;
  /** The meeting this summary belongs to, shown as context beneath the heading. */
  meetingTitle: string;
  /** The meeting's own id. `id` is the summary's, which is what the detail route takes. */
  meetingId: string;
  /** Single category from the pinned taxonomy. Null where the item is Miscellaneous. */
  category: string | null;
  /** The SHORT summary. The long one lives on the detail screen. */
  summary: string;
  /**
   * An agenda for a meeting that has not happened yet. Its summaries describe what is
   * scheduled, not what was decided — without this a reader takes "no vote recorded" as a
   * result rather than as a meeting that has not occurred.
   */
  isUpcoming: boolean;
  /** Category tags from the pinned taxonomy. "Miscellaneous" is never surfaced. */
  tags: string[];
  /**
   * The spoken ordinance or resolution number, preserved verbatim (summarizer Rule 3).
   * `null` where none was spoken — the pipeline writes "N/A", which the API normalizes to
   * null. Drives whether a Track / Follow action renders at all.
   */
  num: string | null;
  /**
   * `"Unanimous"`, `"Passed 3-2"`, or null. Non-null means it was voted on; null means it was
   * discussed with no vote. Never infer this — the field is authoritative.
   */
  vote: string | null;
  /**
   * Provenance for the item as a whole. See `Citation` — this product cites to a passage in
   * the transcript, not to a timestamp in a recording.
   */
  citation: Citation | null;
  /**
   * True once this meeting's number is already part of a Legislative History record, so the
   * row shows "in this record" instead of "Track".
   */
  inRecord?: boolean;
}

/** The always-present first chip: every followed town, newest first. */
export const LATEST_TOWN_ID = 'latest';

export interface AwareExplain {
  meetingId: string;
  /** The simplified text that replaces the summary in place. */
  text: string;
  /**
   * True when this view did not cost the user a metered use — either they had already
   * explained this item, or it is a repeat view. The meter is charged on first view per user,
   * not per call, so a globally cached explanation can still be free for this reader.
   */
  free: boolean;
}

/**
 * The full meeting article plus its structured extractions.
 *
 * The structured half (votes, dollar figures, named bodies) is what a pro tier sees in the
 * rail; a consumer sees the article and Aware Explain and nothing else. The API returns the
 * whole object either way and the UI decides — entitlement for *reading* a meeting is the
 * same at every tier, only the presentation differs.
 */
export interface MeetingDetail extends Meeting {
  /** The LONG summary, split on its existing blank lines. Never rewritten. */
  paragraphs: string[];
  /** A pulled quote from the discussion, already attributed. */
  quote: { text: string; attribution: string; citation: Citation | null } | null;
  /**
   * The meeting transcript. Immutable at the database level — `meetings.transcript_text` is
   * append-only and guarded by a trigger. Citations index into this string.
   */
  transcript: string;
  votes: VoteRecord[];
  dollarFigures: DollarFigure[];
  /**
   * People and permanent places named in the meeting. Plain text, NEVER clickable — the
   * Entities surface was removed because the extractor covers civic-role people and permanent
   * places only, so there is no entity page to link to. Do not turn these into links.
   *
   * Public commenters are anonymous by Rule 4A and must never appear here as named people.
   */
  namedInMeeting: NamedThing[];
}

/**
 * A citation: where in the source a fact came from.
 *
 * **This product cites to a passage, not to a timestamp.** Every extracted object carries an
 * `evidence_quote` — a verbatim transcript substring of at least twelve words — and the
 * pipeline locates it in `transcript_text` to compute exact character offsets in code, because
 * models are unreliable at producing raw offsets themselves.
 *
 * Per-item `start_time` (seconds into the recording) is a planned pipeline addition that does
 * not exist yet. Do not reintroduce timestamps into this UI until it ships; rendering a
 * position the pipeline never produced is worse than citing the sentence.
 */
export interface Citation {
  /** The verbatim sentence, exactly as spoken. */
  quote: string;
  /** Character offsets into `MeetingDetail.transcript`. */
  charStart: number;
  charEnd: number;
}

export interface VoteRecord {
  item: string;
  /** Read from the pipeline's `vote` field, never inferred. */
  outcome: 'PASSED' | 'TABLED' | 'FAILED';
  /** e.g. "7–0", or null where the record has no roll call. */
  tally: string | null;
  citation: Citation | null;
}

export interface DollarFigure {
  amount: string;
  label: string;
  citation: Citation | null;
}

export interface NamedThing {
  name: string;
  kind: 'body' | 'person' | 'place';
}
